import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';
import { useLang } from '../i18n';

const SCENES = [
    {
        year: '2022',
        img: 'https://static.prod-images.emergentagent.com/jobs/c312d019-e509-40a5-b411-a1f75defbc2a/images/a35c638028618b7356f00070aba45bc9888786e5f4559c90135c7a18a0c378f6.jpeg',
        alt: 'Ilustración a tinta de dos mundos paralelos: la hostelería mediterránea y un escritorio ordenado con números',
    },
    {
        year: '2023',
        img: 'https://static.prod-images.emergentagent.com/jobs/c312d019-e509-40a5-b411-a1f75defbc2a/images/7b58b751329fa837d9a7a93d6e49fb82533f75cabf53bfaeb210017962c575bc.jpeg',
        alt: 'Ilustración a tinta de un hilo rojo que une una taza de café con un maletín de empresa',
    },
    {
        year: '2023-2024',
        img: 'https://static.prod-images.emergentagent.com/jobs/c312d019-e509-40a5-b411-a1f75defbc2a/images/7fae58382f666e60b9a613902b045c539f7ed24076856658e2d4a1b89df4946f.jpeg',
        alt: 'Ilustración a tinta de una pizarra llena de ideas, cafés compartidos y manos colaborando',
    },
    {
        year: '2025',
        img: 'https://static.prod-images.emergentagent.com/jobs/c312d019-e509-40a5-b411-a1f75defbc2a/images/438f7f321d832d6c72849a2c77b869939a40c2a2e5d9cc8c66a6f0193d5ca7a3.jpeg',
        alt: 'Ilustración a tinta de manos firmando documentos con alianzas de boda al lado',
    },
    {
        year: '2026',
        img: 'https://static.prod-images.emergentagent.com/jobs/c312d019-e509-40a5-b411-a1f75defbc2a/images/a83af94a00eeb2429c6df4570ddb9a3b7742a2506fc88ab6ace1920f251134d3.jpeg',
        alt: 'Ilustración a tinta de un equipo diverso liderando una mesa de trabajo luminosa al amanecer',
    },
];

export default function Story() {
    const { t } = useLang();
    const [active, setActive] = useState(0);
    const scene = SCENES[active];
    const prev = () => setActive((a) => (a - 1 + SCENES.length) % SCENES.length);
    const next = () => setActive((a) => (a + 1) % SCENES.length);

    return (
        <section id="historia" className="relative py-24 lg:py-32 bg-canvas overflow-hidden" data-testid="historia-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeading eyebrow={t('story_eyebrow')} title={t('story_title')} />

                <Reveal delay={0.1}>
                    <div className="mt-14 flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2" data-testid="historia-tabs">
                        {SCENES.map((s, i) => (
                            <button
                                key={s.year}
                                onClick={() => setActive(i)}
                                className={`group shrink-0 flex items-center gap-2.5 rounded-full pl-3 pr-5 py-2 border transition-all duration-300 ${active === i ? 'bg-ink text-pastelcream border-ink' : 'bg-white border-ink/15 text-ink/60 hover:border-crimson hover:text-crimson'}`}
                                data-testid={`historia-tab-${s.year}`}
                            >
                                <span className={`w-2 h-2 rounded-full ${active === i ? 'bg-crimson' : 'bg-ink/25 group-hover:bg-crimson'} transition-colors`} />
                                <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.1em] uppercase whitespace-nowrap">
                                    {s.year} · {t(`story${i + 1}_label`)}
                                </span>
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="relative min-h-[520px] sm:min-h-[440px] mt-10">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -18 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
                            data-testid={`historia-panel-${scene.year}`}
                        >
                            <div className="lg:col-span-5">
                                <div className="relative rounded-[1.6rem] overflow-hidden border border-ink/15 shadow-[0_30px_60px_-30px_rgba(27,42,74,0.4)]">
                                    <img
                                        src={scene.img}
                                        alt={scene.alt}
                                        className="w-full aspect-square object-cover"
                                        data-testid="historia-image"
                                    />
                                </div>
                            </div>

                            <div className="lg:col-span-7">
                                <p className="font-ink text-[11px] uppercase tracking-[0.3em] text-crimson" data-testid="historia-scene-number">
                                    {t('scene_word')} {String(active + 1).padStart(2, '0')} · {scene.year}
                                </p>
                                <p className="font-wordmark text-sm tracking-[0.2em] text-ink/50 mt-6">{t(`story${active + 1}_label`).toUpperCase()}</p>
                                <h3 className="font-display text-3xl lg:text-4xl font-semibold text-ink mt-3">{t(`story${active + 1}_title`)}</h3>
                                <p className="mt-6 text-base sm:text-lg leading-relaxed text-ink/75 max-w-2xl" data-testid="historia-scene-text">
                                    {t(`story${active + 1}_text`)}
                                </p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <Reveal delay={0.1}>
                    <div className="mt-10 flex items-center justify-between border-t border-ink/10 pt-6">
                        <span className="font-ink text-[10px] uppercase tracking-[0.3em] text-ink/40">
                            {String(active + 1).padStart(2, '0')} / {String(SCENES.length).padStart(2, '0')}
                        </span>
                        <div className="flex gap-3">
                            <button onClick={prev} aria-label="Escena anterior" className="w-11 h-11 rounded-full border border-ink/15 flex items-center justify-center text-ink hover:border-crimson hover:text-crimson transition-colors" data-testid="historia-prev">
                                <ArrowLeft size={17} />
                            </button>
                            <button onClick={next} aria-label="Escena siguiente" className="w-11 h-11 rounded-full bg-ink text-pastelcream flex items-center justify-center hover:bg-crimson transition-colors" data-testid="historia-next">
                                <ArrowRight size={17} />
                            </button>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
